import { AxiosError } from 'axios';
import createNewApiClient from '../apiClient';
import {
  FlightBriefingErrorBody,
  FlightBriefingParams,
} from '../../@types/FlightBriefing.type';

export const FLIGHT_BRIEFING_DOCUMENT_ENDPOINT =
  '/jetstream/api/1/getflightbriefingdocument';

const REQUEST_TIMEOUT_MS = 30000;

export class FlightBriefingError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'FlightBriefingError';
    this.status = status;
  }
}

/** Extracts the API `message` from a JSON (or blob-wrapped JSON) error body. */
export const extractErrorMessage = async (
  data: unknown,
): Promise<string | undefined> => {
  try {
    let body: FlightBriefingErrorBody | undefined;
    if (data instanceof Blob) {
      const text = await data.text();
      body = text ? (JSON.parse(text) as FlightBriefingErrorBody) : undefined;
    } else if (typeof data === 'string') {
      body = JSON.parse(data) as FlightBriefingErrorBody;
    } else if (data && typeof data === 'object') {
      body = data as FlightBriefingErrorBody;
    }
    return body?.message;
  } catch {
    return undefined;
  }
};

const ACCEPT_HEADER: Record<string, string> = {
  pdf: 'application/pdf, application/json',
  xml: 'application/xml, text/xml, application/json',
};

export const getDocumentUrl = (packetType: string) =>
  `${FLIGHT_BRIEFING_DOCUMENT_ENDPOINT}/${encodeURIComponent(packetType)}`;

/** A successful response must contain a non-empty document, not an error/HTML page. */
const assertValidDocument = async (blob: unknown): Promise<Blob> => {
  if (!(blob instanceof Blob) || blob.size === 0) {
    throw new FlightBriefingError('The API returned an empty document.');
  }
  if (/json|html/i.test(blob.type)) {
    const apiMessage = await extractErrorMessage(
      /json/i.test(blob.type) ? blob : undefined,
    );
    throw new FlightBriefingError(
      apiMessage ?? 'The API returned an invalid document.',
    );
  }
  return blob;
};

const toFlightBriefingError = async (
  error: unknown,
): Promise<FlightBriefingError> => {
  if (error instanceof FlightBriefingError) return error;

  const axiosError = error as AxiosError;
  const status = axiosError?.response?.status;
  if (status) {
    const apiMessage = await extractErrorMessage(axiosError.response?.data);
    return new FlightBriefingError(
      apiMessage ?? `The request failed (HTTP ${status}).`,
      status,
    );
  }
  if (axiosError?.code === 'ECONNABORTED' || axiosError?.code === 'ETIMEDOUT') {
    return new FlightBriefingError('The request timed out. Please try again.');
  }
  return new FlightBriefingError(
    'Unable to reach the JetStream API. Check your connection and try again.',
  );
};

/**
 * Downloads one flight briefing document.
 * GET {APIM_URL}/jetstream/api/1/getflightbriefingdocument/{packetType}
 *     ?operatingAirline&scheduledOperatingDateUTC&flightNumber&origin&destination
 */
export const getFlightBriefingDocument = async (
  packetType: string,
  params: FlightBriefingParams,
  accessToken: string,
): Promise<Blob> => {
  const apiClient = createNewApiClient(accessToken);
  const format = packetType.split('-')[0];

  try {
    const response = await apiClient.get<Blob>(getDocumentUrl(packetType), {
      params,
      responseType: 'blob',
      timeout: REQUEST_TIMEOUT_MS,
      headers: { Accept: ACCEPT_HEADER[format] ?? '*/*' },
    });
    return await assertValidDocument(response.data);
  } catch (error) {
    throw await toFlightBriefingError(error);
  }
};
